import CircularProgress from '@material-ui/core/CircularProgress';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import React, { lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SubHeader from 'globalcomponents/SubHeader/Clutch';
import Button from 'globalcomponents/UI/Button/Clutch';
import Label from 'globalcomponents/UI/FormLabel/Clutch';
import Link from 'globalcomponents/UI/Link';
import Message from 'globalcomponents/UI/Message/Clutch';
import { getMediaPath, mediaUrls } from 'globalconfig/constants/mediaUrls';
import { pageUrls } from 'globalconfig/constants/keys';
import { SvgIcon } from '@material-ui/core';
import MicrosoftLoginButton from './components/MicrosoftLoginButton';

const layout = window.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const CheckBox = lazy(() => import(`globalcomponents/UI/Checkbox/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;

const UsernameField = ({ values, handleEdit, errors, isNotValid, i18n }) => (
  <FormStyle.FieldsWrapper width="100%">
    <Label variant="body1" mode="light" className="labelSize">
      {i18n.t('Username')}
    </Label>
    <FormStyle.TextWrapper mode="light">
      <Field
        name="username"
        as={Input}
        placeholder={i18n.t('Username or Email')}
        value={values.username}
        onLoadFocus
        type="text"
        id="usernameLogin"
        handleOnChange={(event) => handleEdit(event, { field: 'username', type: 'username' })}
        mode="light"
      />
      {errors.username && !isNotValid && <Message type="error" message={errors.username} />}
    </FormStyle.TextWrapper>
  </FormStyle.FieldsWrapper>
);

const PasswordField = ({ values, handleEdit, errors, isNotValid, i18n }) => {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <FormStyle.FieldsWrapper width="100%">
      <Label variant="body1" mode="light">
        {i18n.t('common.password')}
      </Label>
      <FormStyle.TextWrapper className="passwordField">
        <Field
          name="password"
          as={Input}
          placeholder={i18n.t('common.password')}
          value={values.password}
          type={showPassword ? 'text' : 'password'}
          id="loginPassword"
          handleOnChange={(event) => handleEdit(event, { field: 'password', type: 'password' })}
          mode="light"
        />
        <SvgIcon value={mediaUrls.viewMoreIcon} alt="toggle visibility" onClick={() => setShowPassword((prev) => !prev)} />
        {errors.password && !isNotValid && <Message type="error" message={errors.password} />}
      </FormStyle.TextWrapper>
    </FormStyle.FieldsWrapper>
  );
};

const RememberMeAndForgot = ({ values, handleEdit, i18n, Styled }) => (
  <Styled.PasswordHandler>
    <CheckBox
      type={!window.$environment.WHITE_THEME && window.$environment.SERVICE_PROVIDER !== 'simplex' ? 'NormalGrey' : 'NormalWhite'}
      checked={values.rememberMe}
      className="remember"
      label={i18n.t('Remember me')}
      onClick={(event) => handleEdit(event, { field: 'rememberMe', type: 'checkbox' })}
    />
    <Link href={pageUrls.recovery}>{i18n.t('Forgot password?')}</Link>
  </Styled.PasswordHandler>
);

const LoginButton = ({ isLoading, validateForm, submitAction, i18n }) => (
  <FormStyle.ButtonsWrapper>
    <Button
      type="submit"
      label={isLoading ? <CircularProgress color="inherit" /> : i18n.t('Login')}
      onClick={() => {
        validateForm().then((data) => {
          submitAction(data);
        });
      }}
      id="loginButton"
    />
  </FormStyle.ButtonsWrapper>
);

const RegisterSection = ({ Styled }) => {
  const shouldShow = window.$environment.SERVICE_PROVIDER === 'clutch' || window.$environment.SERVICE_PROVIDER === 'at';
  return shouldShow ? (
    <Styled.registerSection>
      <span className="registerLabel">Don't have an account?</span>
      <a href="/registration" className="linkToregister">
        Click here to Register
      </a>
    </Styled.registerSection>
  ) : null;
};

const RenderComponent = ({ values, handleEdit, errors, isNotValid, validateForm, customProps, submitAction }) => {
  const { i18n } = useTranslation();
  const { hasLoginApiStatus, hasMicrosoftLoginApiStatus, Styled, history, fetchMicrosoftLogin, webAppConfigurations } = customProps;

  return (
    <>
      <Styled.LoginHeader>
        <img alt="logo" src={getMediaPath(mediaUrls.clutchLogo, true)} className="ClutchLogo" />
        <SubHeader variant="h2" text={i18n.t('Login to your account')} mode="light" className="loginHeader" />
      </Styled.LoginHeader>

      {(hasMicrosoftLoginApiStatus?.errorMessage || hasLoginApiStatus?.errorMessage) && (
        <Styled.LoginError>
          <Message
            type="error"
            message={hasMicrosoftLoginApiStatus?.errorMessage || hasLoginApiStatus?.errorMessage}
          />
        </Styled.LoginError>
      )}

      <Styled.userNameWrapper>
        <UsernameField values={values} handleEdit={handleEdit} errors={errors} isNotValid={isNotValid} i18n={i18n} />
      </Styled.userNameWrapper>

      <Styled.passwordWrapper>
        <PasswordField values={values} handleEdit={handleEdit} errors={errors} isNotValid={isNotValid} i18n={i18n} />
      </Styled.passwordWrapper>

      <RememberMeAndForgot values={values} handleEdit={handleEdit} i18n={i18n} Styled={Styled} />

      <Styled.LoginButtonWrapper>
        <LoginButton
          isLoading={hasLoginApiStatus?.isLoading}
          validateForm={validateForm}
          submitAction={submitAction}
          i18n={i18n}
        />
      </Styled.LoginButtonWrapper>

      {/* Microsoft SSO Login */}
      <MicrosoftLoginButton
        history={history}
        fetchMicrosoftLogin={fetchMicrosoftLogin}
        i18n={i18n}
        Styled={Styled}
        webAppConfigurations={webAppConfigurations}
        hasMicrosoftLoginApiStatus={hasMicrosoftLoginApiStatus}
      />

      <RegisterSection Styled={Styled} />
    </>
  );
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  customProps: PropTypes.shape({
    hasLoginApiStatus: PropTypes.object,
    hasMicrosoftLoginApiStatus: PropTypes.object,
    Styled: PropTypes.object.isRequired,
    history: PropTypes.object,
    fetchMicrosoftLogin: PropTypes.func,
    webAppConfigurations: PropTypes.object,
  }),
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
};

export default RenderComponent;
